import {
  createAction,
  createSelector,
  type PayloadAction,
  type UnknownAction
} from "@reduxjs/toolkit";
import type { PrefillBookingType } from "../../components/Pages/Diary/Sidebar/AddBooking/AddBooking";
import type { ISelectedFilters } from "../../components/Pages/Diary/Sidebar/DiaryFilters/DiaryFilters";
import type { ToggleView } from "../../components/Pages/Diary/SubHeader/SubHeader";
import type { Enquiry } from "../../types/enquiries";
import type { BookableArea, Floorplan } from "../../types/venues";
import type { ViewBooking } from "collins-js-sdk";
import { floorPlanConfigStorageDefault } from "../../utils/localStorage/floorPlanConfigStorage";
import type { GridConfigDataStorageType } from "../../utils/localStorage/gridConfigStorage";
import { gridConfigStorageDefault } from "../../utils/localStorage/gridConfigStorage";
import type { RunsheetConfigDataStorageType } from "../../utils/localStorage/runsheetConfigStorage";
import { runsheetConfigStorageDefault } from "../../utils/localStorage/runsheetConfigStorage";
import type { RootState } from "../store";

export type SelectedTableFilterType = {
  name: string;
  id: string;
};

export type FloorPlanConfigType = {
  displayNonTableItems: boolean;
  displayTableNames: boolean;
  zoomLevel: number;
};

export type RunsheetConfigType = RunsheetConfigDataStorageType;

export type GridConfigOptionsType = {
  displayCurrentTime: boolean;
  startMinutes: number;
  endMinutes: number;
  colMinutes: number;
  cellHeight: number;
  cellWidth: number;
  areaPanelWidth: number;
  maxRowsForArea: number;
  mode?: "desktop" | "mobile";
  bookingCardView?: "Modal" | "PopOut";
  height?: string;
  width?: string;
};

export type GridConfigType = GridConfigOptionsType & GridConfigDataStorageType;

export type DiaryState = {
  selectedVenue?: string;
  /**
   * Venue whose config the diary is showing (read it with `useVenueConfig`).
   * `undefined` until the first config loads; `null` once cleared because the
   * organisation has no venues or was switched.
   */
  loadedVenueId?: string | null;
  diaryFilters?: ISelectedFilters;
  date: string;
  isInitialising: boolean;
  /** False until saved grid size and opening hours have been applied. */
  isGridLayoutReady?: boolean;
  isViewTransitioning: boolean;
  gridConfig: GridConfigType;
  selectedBooking?: ViewBooking;
  highlightArea?: string[];
  selectedView: ToggleView;
  floorPlanConfig: FloorPlanConfigType;
  runsheetConfig: RunsheetConfigType;
  selectedTable?: SelectedTableFilterType;
  lastUpdatedTable?: BookableArea;
  showSidebar: boolean;
  editingFromPreviewModal?: boolean;
  selectedFloorplan?: Floorplan;
  editBookingId?: string;
  selectedBookingId?: string;
  openedFromEnquiry?: boolean;
  openedFromSpotlight?: boolean;
  spotlightRequestedTabKey?: string;
  selectedEnquiry?: Enquiry;
  enquiriesReturnSearch?: string;
  isDeletePending?: boolean;
  /** Add Booking panel open in the diary sidebar. */
  showAddBooking: boolean;
  /** Prefill for the Add Booking panel (last-used type and/or party size). */
  prefillBookingType?: PrefillBookingType | null;
};

export type DiaryPatch = Partial<DiaryState>;

export const shallowEqual = (
  obj1: Record<string, unknown>,
  obj2: Record<string, unknown>
): boolean => {
  if (obj1 === obj2) return true;
  const keys1 = Object.keys(obj1);
  if (keys1.length !== Object.keys(obj2).length) return false;
  return keys1.every((key) => obj1[key] === obj2[key]);
};

const pad = (value: number, length = 2) => String(value).padStart(length, "0");

/**
 * Serialises a Date as an ISO string from its UTC fields. Invalid dates map to
 * "" so they read back as Invalid Date. Not built on toISOString, which tests
 * stub to emulate other time zones.
 */
export const toDiaryDateIso = (date: Date): string => {
  if (Number.isNaN(date.getTime())) return "";
  return `${pad(date.getUTCFullYear(), 4)}-${pad(date.getUTCMonth() + 1)}-${pad(
    date.getUTCDate()
  )}T${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}:${pad(
    date.getUTCSeconds()
  )}.${pad(date.getUTCMilliseconds(), 3)}Z`;
};

export const toDiaryDateKey = (date: Date | undefined): string | undefined =>
  date === undefined || Number.isNaN(date.getTime())
    ? undefined
    : date.toISOString().split("T")[0];

export const gridConfigDefault: GridConfigType = {
  ...gridConfigStorageDefault,
  mode: "mobile",
  bookingCardView: "PopOut",
  displayCurrentTime: true,
  startMinutes: 540,
  endMinutes: 1320,
  colMinutes: 15,
  cellHeight: 40,
  cellWidth: 40,
  areaPanelWidth: 128,
  maxRowsForArea: 3,
  width: "0px",
  height: "100%"
};

const floorPlanDefault: FloorPlanConfigType = {
  ...floorPlanConfigStorageDefault
};

const runsheetDefault: RunsheetConfigType = {
  ...runsheetConfigStorageDefault
};

export const initialDiaryState: DiaryState = {
  selectedVenue: undefined,
  loadedVenueId: undefined,
  diaryFilters: undefined,
  date: toDiaryDateIso(new Date()),
  isInitialising: true,
  isGridLayoutReady: false,
  isViewTransitioning: false,
  gridConfig: gridConfigDefault,
  selectedBooking: undefined,
  selectedView: "DiaryTab",
  floorPlanConfig: floorPlanDefault,
  runsheetConfig: runsheetDefault,
  selectedTable: undefined,
  lastUpdatedTable: undefined,
  showSidebar: true,
  editingFromPreviewModal: false,
  openedFromEnquiry: false,
  openedFromSpotlight: false,
  spotlightRequestedTabKey: undefined,
  selectedEnquiry: undefined,
  enquiriesReturnSearch: undefined,
  isDeletePending: false,
  showAddBooking: false,
  prefillBookingType: undefined
};

type AddBookingPatch = Partial<
  Pick<DiaryState, "showSidebar" | "showAddBooking" | "prefillBookingType">
>;

/** State patch applied by openAddBooking; exported so mocks reuse the logic. */
export const openAddBookingPatch = (
  prev: Pick<Partial<DiaryState>, "prefillBookingType">,
  numPeople?: number
): AddBookingPatch => ({
  showSidebar: true,
  showAddBooking: true,
  // Keep the last-used booking type and just set the party size, so the 1-5
  // shortcuts are fast for repeat same-type walk-ins. No prior type yet -> the
  // count alone is prefilled.
  ...(numPeople
    ? { prefillBookingType: { ...prev.prefillBookingType, numPeople } }
    : {})
});

/** Shallow-merges a partial update, preserving the compatibility setter's semantics. */
export const patched = createAction<DiaryPatch>("diary/patched");
export const addBookingOpened = createAction<number | undefined>(
  "diary/addBookingOpened"
);
export const bookingSelected = createAction<DiaryState["selectedBooking"]>(
  "diary/bookingSelected"
);
export const tableFiltered = createAction<DiaryState["selectedTable"]>(
  "diary/tableFiltered"
);
export const floorplanSelected = createAction<DiaryState["selectedFloorplan"]>(
  "diary/floorplanSelected"
);
export const lastUpdatedTableChanged = createAction<
  DiaryState["lastUpdatedTable"]
>("diary/lastUpdatedTableChanged");
export const editBookingRequested = createAction<DiaryState["editBookingId"]>(
  "diary/editBookingRequested"
);
export const sidebarToggled = createAction<boolean>("diary/sidebarToggled");
export const addBookingToggled = createAction<boolean>(
  "diary/addBookingToggled"
);
export const prefillBookingTypeChanged = createAction<
  DiaryState["prefillBookingType"]
>("diary/prefillBookingTypeChanged");
/** The Add Booking panel was closed for good: hide it and drop its prefill. */
export const addBookingReset = createAction("diary/addBookingReset");
export const spotlightTabConsumed = createAction("diary/spotlightTabConsumed");
/**
 * Merges saved fields into the open booking when it is the one edited, so a
 * stage change made from a diary card reaches the booking panel straight away.
 */
export const selectedBookingPatched = createAction<{
  id: string;
  changes: Partial<NonNullable<DiaryState["selectedBooking"]>>;
}>("diary/selectedBookingPatched");
/** The diary moved to another day: show the loading state and close the open booking. */
export const dateChanged = createAction<string>("diary/dateChanged");
export const gridConfigChanged = createAction<DiaryState["gridConfig"]>(
  "diary/gridConfigChanged"
);
export const floorPlanConfigChanged = createAction<
  DiaryState["floorPlanConfig"]
>("diary/floorPlanConfigChanged");
export const runsheetConfigChanged = createAction<DiaryState["runsheetConfig"]>(
  "diary/runsheetConfigChanged"
);
/** Bookings loaded with these filters, ending the initial load; `gridConfig` when opening hours moved. */
export const diaryFiltersApplied = createAction<
  Pick<DiaryState, "diaryFilters"> & Partial<Pick<DiaryState, "gridConfig">>
>("diary/diaryFiltersApplied");
export const initialisingFinished = createAction("diary/initialisingFinished");
/** Saved layouts were restored (or there was nothing to restore). */
export const gridLayoutRestored = createAction<
  Partial<Pick<DiaryState, "gridConfig" | "floorPlanConfig" | "runsheetConfig">>
>("diary/gridLayoutRestored");
export const highlightAreaChanged = createAction<DiaryState["highlightArea"]>(
  "diary/highlightAreaChanged"
);
export const selectedBookingMerged = createAction<
  Partial<NonNullable<DiaryState["selectedBooking"]>> & { id?: string }
>("diary/selectedBookingMerged");
export const selectedEnquiryChanged = createAction<
  DiaryState["selectedEnquiry"]
>("diary/selectedEnquiryChanged");
export const enquiryDetailsOpened = createAction<
  Pick<DiaryState, "selectedEnquiry" | "enquiriesReturnSearch">
>("diary/enquiryDetailsOpened");
export const deletePendingChanged = createAction<boolean>(
  "diary/deletePendingChanged"
);
export const enquiryTaken =
  createAction<Pick<DiaryState, "selectedEnquiry" | "selectedBooking">>(
    "diary/enquiryTaken"
  );
export const enquiryDetailsClosed = createAction<
  Pick<DiaryState, "selectedEnquiry" | "enquiriesReturnSearch">
>("diary/enquiryDetailsClosed");
export const bookingDetailsClosed = createAction<
  Pick<
    DiaryState,
    | "selectedBooking"
    | "selectedBookingId"
    | "openedFromEnquiry"
    | "openedFromSpotlight"
    | "selectedEnquiry"
    | "enquiriesReturnSearch"
  >
>("diary/bookingDetailsClosed");
export const followUpSaved = createAction<
  Pick<DiaryState, "selectedBooking" | "selectedEnquiry">
>("diary/followUpSaved");
export const enquiryAssigned = createAction<
  Pick<DiaryState, "selectedBooking" | "selectedEnquiry">
>("diary/enquiryAssigned");
export const followUpCleared = createAction<
  Pick<DiaryState, "selectedBooking" | "selectedEnquiry">
>("diary/followUpCleared");
export const bookingCopied = createAction<
  Pick<
    DiaryState,
    | "selectedBooking"
    | "selectedBookingId"
    | "showSidebar"
    | "showAddBooking"
    | "prefillBookingType"
  >
>("diary/bookingCopied");
export const editBookingClosed = createAction<
  | Pick<DiaryState, "editingFromPreviewModal">
  | Pick<
      DiaryState,
      "editingFromPreviewModal" | "selectedView" | "selectedBooking"
    >
>("diary/editBookingClosed");
export const selectedBookingDeleted = createAction<
  Pick<DiaryState, "selectedBooking" | "isDeletePending">
>("diary/selectedBookingDeleted");
/** Edit was chosen in the runsheet preview modal: select the booking and mark the edit as coming from the preview. */
export const previewBookingEditOpened = createAction<
  NonNullable<DiaryState["selectedBooking"]>
>("diary/previewBookingEditOpened");
export const noVenuesFound = createAction("diary/noVenuesFound");
export const defaultVenueChosen = createAction<DiaryState["selectedVenue"]>(
  "diary/defaultVenueChosen"
);
export const venueConfigLoaded = createAction<DiaryState["selectedVenue"]>(
  "diary/venueConfigLoaded"
);
export const venueStateReset = createAction("diary/venueStateReset");
export const bookingRouteLeft = createAction("diary/bookingRouteLeft");
export const bookingRouteOpened = createAction<
  Pick<DiaryState, "selectedBookingId"> &
    Partial<Pick<DiaryState, "selectedBooking">>
>("diary/bookingRouteOpened");
export const urlStateSynced = createAction<
  Partial<
    Pick<
      DiaryState,
      | "selectedVenue"
      | "isInitialising"
      | "selectedBooking"
      | "loadedVenueId"
      | "date"
      | "selectedView"
    >
  >
>("diary/urlStateSynced");

const merge = (state: DiaryState, patch: DiaryPatch): DiaryState => {
  const merged = { ...state, ...patch };
  return shallowEqual(state, merged) ? state : merged;
};

type PatchBuilder = (
  state: DiaryState,
  action: UnknownAction
) => DiaryPatch | undefined;

const on = <P>(
  creator: {
    type: string;
    match: (action: unknown) => action is PayloadAction<P>;
  },
  build: (payload: P, state: DiaryState) => DiaryPatch | undefined
): [string, PatchBuilder] => [
  creator.type,
  (state, action) =>
    creator.match(action) ? build(action.payload, state) : undefined
];

const namedActionPatches = new Map<string, PatchBuilder>([
  on(selectedBookingPatched, ({ id, changes }, state) =>
    state.selectedBooking?.id === id
      ? { selectedBooking: { ...state.selectedBooking, ...changes } }
      : undefined
  ),
  on(bookingSelected, (selectedBooking) => ({ selectedBooking })),
  on(tableFiltered, (selectedTable) => ({ selectedTable })),
  on(floorplanSelected, (selectedFloorplan) => ({ selectedFloorplan })),
  on(lastUpdatedTableChanged, (lastUpdatedTable) => ({ lastUpdatedTable })),
  on(editBookingRequested, (editBookingId) => ({ editBookingId })),
  on(sidebarToggled, (showSidebar) => ({ showSidebar })),
  on(addBookingToggled, (showAddBooking) => ({ showAddBooking })),
  on(prefillBookingTypeChanged, (prefillBookingType) => ({
    prefillBookingType
  })),
  on(addBookingReset, () => ({
    showAddBooking: false,
    prefillBookingType: undefined
  })),
  on(spotlightTabConsumed, () => ({ spotlightRequestedTabKey: undefined })),
  on(dateChanged, (date) => ({
    date,
    isInitialising: true,
    selectedBooking: undefined
  })),
  on(gridConfigChanged, (gridConfig) => ({ gridConfig })),
  on(floorPlanConfigChanged, (floorPlanConfig) => ({ floorPlanConfig })),
  on(runsheetConfigChanged, (runsheetConfig) => ({ runsheetConfig })),
  on(diaryFiltersApplied, (payload) => ({ ...payload, isInitialising: false })),
  on(initialisingFinished, () => ({ isInitialising: false })),
  on(gridLayoutRestored, (payload) => ({
    ...payload,
    isGridLayoutReady: true
  })),
  on(highlightAreaChanged, (highlightArea) => ({ highlightArea })),
  on(selectedBookingMerged, (changes, state) =>
    state.selectedBooking?.id && state.selectedBooking.id === changes.id
      ? { selectedBooking: { ...state.selectedBooking, ...changes } }
      : undefined
  ),
  on(selectedEnquiryChanged, (selectedEnquiry) => ({ selectedEnquiry })),
  on(enquiryDetailsOpened, (payload) => ({
    ...payload,
    openedFromEnquiry: true
  })),
  on(deletePendingChanged, (isDeletePending) => ({ isDeletePending })),
  on(enquiryTaken, (payload) => payload),
  on(enquiryDetailsClosed, (payload) => payload),
  on(bookingDetailsClosed, (payload) => payload),
  on(followUpSaved, (payload) => payload),
  on(enquiryAssigned, (payload) => payload),
  on(followUpCleared, (payload) => payload),
  on(bookingCopied, (payload) => payload),
  on(editBookingClosed, (payload) => payload),
  on(selectedBookingDeleted, (payload) => payload),
  on(previewBookingEditOpened, (selectedBooking) => ({
    selectedBooking,
    editingFromPreviewModal: true
  })),
  on(noVenuesFound, () => ({
    selectedVenue: undefined,
    loadedVenueId: null,
    isInitialising: false
  })),
  on(defaultVenueChosen, (selectedVenue) => ({ selectedVenue })),
  on(venueConfigLoaded, (loadedVenueId) => ({ loadedVenueId })),
  on(venueStateReset, () => ({
    selectedVenue: undefined,
    loadedVenueId: null
  })),
  on(bookingRouteLeft, () => ({
    selectedBookingId: undefined,
    selectedBooking: undefined,
    openedFromEnquiry: false,
    openedFromSpotlight: false
  })),
  on(bookingRouteOpened, (payload) => payload),
  on(urlStateSynced, (payload) => payload)
]);

const namedActionPatch = (
  state: DiaryState,
  action: UnknownAction
): DiaryPatch | undefined =>
  namedActionPatches.get(action.type)?.(state, action);
/**
 * A plain reducer rather than `createSlice`: Immer would deep-freeze
 * `selectedBooking`, which is usually the same object as an item in the
 * bookings-list query cache that existing code still edits in place.
 */
export const diaryReducer = (
  state: DiaryState = initialDiaryState,
  action: UnknownAction
): DiaryState => {
  if (patched.match(action)) return merge(state, action.payload);
  if (addBookingOpened.match(action)) {
    return merge(state, openAddBookingPatch(state, action.payload));
  }
  const patch = namedActionPatch(state, action);
  return patch ? merge(state, patch) : state;
};

export const selectDiary = (state: RootState): DiaryState => state.diary;
export const selectDiaryDateIso = (state: RootState): string =>
  state.diary.date;
export const selectDiaryDate = createSelector(
  [selectDiaryDateIso],
  (iso) => new Date(iso)
);
export const selectSelectedVenue = (state: RootState) =>
  state.diary.selectedVenue;
export const selectSelectedView = (state: RootState) =>
  state.diary.selectedView;
export const selectSelectedBooking = (state: RootState) =>
  state.diary.selectedBooking;
export const selectSelectedBookingId = (state: RootState) =>
  state.diary.selectedBookingId;
export const selectSelectedBookingRecordId = (state: RootState) =>
  state.diary.selectedBooking?.id;
export const selectSelectedBookingCustomerId = (state: RootState) =>
  state.diary.selectedBooking?.customerId;
export const selectShowSidebar = (state: RootState) => state.diary.showSidebar;
export const selectShowAddBooking = (state: RootState) =>
  state.diary.showAddBooking;
export const selectIsInitialising = (state: RootState) =>
  state.diary.isInitialising;
export const selectLoadedVenueId = (state: RootState) =>
  state.diary.loadedVenueId;
export const selectDiaryListTarget = createSelector(
  [selectDiaryDate, selectLoadedVenueId],
  (date, loadedVenueId) => ({ date, loadedVenueId })
);
export const selectDiaryFilters = (state: RootState) =>
  state.diary.diaryFilters;
export const selectIsGridLayoutReady = (state: RootState) =>
  state.diary.isGridLayoutReady;
export const selectIsViewTransitioning = (state: RootState) =>
  state.diary.isViewTransitioning;
export const selectFloorPlanConfig = (state: RootState) =>
  state.diary.floorPlanConfig;
export const selectEditingFromPreviewModal = (state: RootState) =>
  state.diary.editingFromPreviewModal;
export const selectOpenedFromEnquiry = (state: RootState) =>
  state.diary.openedFromEnquiry;
export const selectOpenedFromSpotlight = (state: RootState) =>
  state.diary.openedFromSpotlight;
export const selectSpotlightRequestedTabKey = (state: RootState) =>
  state.diary.spotlightRequestedTabKey;
export const selectSelectedEnquiry = (state: RootState) =>
  state.diary.selectedEnquiry;
export const selectEnquiriesReturnSearch = (state: RootState) =>
  state.diary.enquiriesReturnSearch;
export const selectIsDeletePending = (state: RootState) =>
  state.diary.isDeletePending;
export const selectPrefillBookingType = (state: RootState) =>
  state.diary.prefillBookingType;
export const selectGridConfig = (state: RootState) => state.diary.gridConfig;
export const selectRunsheetConfig = (state: RootState) =>
  state.diary.runsheetConfig;
export const selectSelectedTable = (state: RootState) =>
  state.diary.selectedTable;
export const selectLastUpdatedTable = (state: RootState) =>
  state.diary.lastUpdatedTable;
export const selectEditBookingId = (state: RootState) =>
  state.diary.editBookingId;
/** The sidebar is on screen: open, off the runsheet and not opened from an enquiry. */
export const selectIsSidebarVisible = (state: RootState): boolean =>
  !!state.diary.showSidebar &&
  state.diary.selectedView !== "RunsheetTab" &&
  !state.diary.openedFromEnquiry;

export const selectSelectedFloorplan = (state: RootState) =>
  state.diary.selectedFloorplan;
export const selectHighlightArea = (state: RootState) =>
  state.diary.highlightArea;
/** Add Booking's assigned areas take precedence over the table filter. */
export const selectFloorplanHighlightedAreaIds = createSelector(
  [selectHighlightArea, selectSelectedTable],
  (highlightArea, selectedTable): string[] => {
    if (highlightArea?.length) return highlightArea;
    return selectedTable?.id != null ? [selectedTable.id] : [];
  }
);

export default diaryReducer;
